import React, { createContext, useContext, useEffect, useState } from 'react';
import { userDataService } from '../services/UserDataService';
import { AccessStatus, AuthState, User } from '../types/auth';
import { scheduleDataSync, stopDataSync, syncUserDataWithFirestore } from '../utils/dataSyncUtils';
import { IAuthService } from './interfaces/IAuthService';
import { FirebaseAuthService } from './services/FirebaseAuthService';
import { MockAuthService } from './services/MockAuthService';
import { confirmAction } from '../components/modals/ConfirmDialog';

// Initialize Firebase first
import '../config/firebase';

interface AuthContextType extends AuthState {
  signInWithGoogle: () => Promise<User>;
  signOut: () => Promise<void>;
  authService: IAuthService;
  // Re-evaluates the public-deployment access gate (allowedDomains → allowlist → request).
  // AccessGate calls this after submitting a request so the screen flips to "pending".
  refreshAccessStatus: () => Promise<void>;
}

// Resolve the public-deployment access gate in the order documented in the plan:
// domain (cheapest, covers Workspace users) → individual allowlist → request status.
// Returns 'unknown' only on unauthenticated callers (caller shouldn't ask).
async function resolveAccessStatus(user: User): Promise<AccessStatus> {
  if (await userDataService.isDomainAllowed(user.email)) return 'allowed';
  if (await userDataService.isEmailAllowlisted(user.email)) return 'allowed';
  const requestStatus = await userDataService.getAccessRequestStatus(user.email);
  if (requestStatus === 'approved') return 'allowed'; // race window: approved but admin hasn't created allowlist doc yet
  if (requestStatus === 'pending') return 'pending';
  if (requestStatus === 'denied') return 'denied';
  return 'no_request';
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
  useMock?: boolean;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ 
  children, 
  useMock = import.meta.env.VITE_USE_MOCK_AUTH === 'true' 
}) => {
  // Pluggable: Switch between Firebase and Mock auth
  const [authService] = useState<IAuthService>(() => 
    useMock ? new MockAuthService() : new FirebaseAuthService()
  );

  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    syncing: false,
    error: null,
    accessStatus: 'unknown',
  });

  useEffect(() => {
    // Subscribe to auth state changes
    const unsubscribe = authService.onAuthStateChanged(async (user) => {
      if (user) {
        // Public-deployment access gate. Resolve BEFORE any Firestore writes so
        // non-allowlisted users don't trigger permission-denied noise from
        // ensureUserProfile / syncUserDataWithFirestore.
        const accessStatus = await resolveAccessStatus(user);

        if (accessStatus !== 'allowed') {
          // Signed in but no app access — render AccessGate, skip profile/sync.
          (window as any).__currentUserId = user.uid;
          setAuthState({
            user,
            loading: false,
            syncing: false,
            error: null,
            accessStatus,
          });
          return;
        }

        // Mark sync in flight so the UI can show "Loading your past history…"
        // instead of rendering the dashboard with empty caches.
        setAuthState(prev => ({ ...prev, syncing: true }));

        // Hydrate in-memory caches from IndexedDB (and migrate any legacy
        // localStorage keys). Synchronous getAggregation / queryCompletions
        // callers depend on these being warm before the UI mounts.
        try {
          const [{ aggregationService }, { levelCompletionService }] = await Promise.all([
            import('../services/AggregationService'),
            import('../services/LevelCompletionService'),
          ]);
          await Promise.all([
            aggregationService.hydrate(user.uid),
            levelCompletionService.hydrate(user.uid),
          ]);
        } catch (error) {
          console.warn('Cache hydrate skipped:', error);
        }

        // Get isAdmin flag from Firestore (authoritative source)
        const isAdmin = await userDataService.getIsAdminFromFirestore(user.uid);
        const userWithAdmin = { ...user, isAdmin };

        // Ensure user profile exists in storage
        await userDataService.ensureUserProfile(userWithAdmin);

        // Sync FROM Firestore (pulls all data down to localStorage). MUST be
        // awaited before unblocking the UI — these are the reads that warm
        // the cache for synchronous-read components (StreakBadge,
        // SmartDashboardHome, course-card stars, etc.).
        const syncResult = await syncUserDataWithFirestore(user.uid);

        // Level completions: pull Firestore→local so synchronous
        // queryCompletions() reads (e.g. course-card stars) see the full
        // history on a cold-cache origin.
        try {
          const { levelCompletionService } = await import('../services/LevelCompletionService');
          await levelCompletionService.syncFromFirestore(user.uid);
        } catch (error) {
          console.warn('Level-completion sync skipped:', error);
        }
        if (syncResult.syncPerformed) {
          console.log('🔄 Data sync performed - localStorage updated', syncResult);

          // If any data was cleared, notify user to refresh
          if (syncResult.clearedProgress || syncResult.clearedKeyboards || syncResult.clearedProfile) {
            // Dispatch a custom event that components can listen to
            window.dispatchEvent(new CustomEvent('dataCleared', {
              detail: syncResult
            }));
          }

          // If keyboards were synced down, dispatch keyboard update event
          if (syncResult.syncedKeyboards > 0) {
            console.log(`✅ Synced ${syncResult.syncedKeyboards} keyboards from Firestore`);
            window.dispatchEvent(new CustomEvent('keyboardUpdated', {
              detail: { syncedCount: syncResult.syncedKeyboards }
            }));
          }

          // If daily activities were synced, dispatch event so components can refresh
          if (syncResult.syncedDailyActivities > 0) {
            console.log(`✅ Synced ${syncResult.syncedDailyActivities} daily activities from Firestore`);
            window.dispatchEvent(new CustomEvent('dailyActivitiesSynced', {
              detail: { syncedCount: syncResult.syncedDailyActivities }
            }));
          }

          // If points were synced, the PointsManager already dispatches 'pointsUpdated'
        }

        // Schedule periodic sync checks
        scheduleDataSync(user.uid);

        // Store current user ID globally for admin checks
        (window as any).__currentUserId = user.uid;

        // Cache is warm now — UNBLOCK the UI. Components mount with full data.
        setAuthState({
          user: userWithAdmin,
          loading: false,
          syncing: false,
          error: null,
          accessStatus: 'allowed',
        });

        // Backfills run AFTER the UI is unblocked. They only push local→Firestore
        // and don't change displayed data, so blocking the dashboard on them
        // would just delay the user for no UI benefit. Failures retry on the
        // next login (idempotent via per-user localStorage markers).
        void (async () => {
          try {
            const { backfillDailyActivitiesToFirestore } = await import('../utils/globalStreakUtils');
            await backfillDailyActivitiesToFirestore(user.uid);
          } catch (error) {
            console.warn('Daily-activity backfill skipped:', error);
          }
          try {
            const { aggregationService } = await import('../services/AggregationService');
            await aggregationService.backfillAggregationsToFirestore(user.uid);
          } catch (error) {
            console.warn('Aggregation backfill skipped:', error);
          }
          try {
            const { levelCompletionService } = await import('../services/LevelCompletionService');
            await levelCompletionService.backfillToFirestore(user.uid);
          } catch (error) {
            console.warn('Level-completion backfill skipped:', error);
          }
          try {
            const { runParticipantUserIdsBackfillIfNeeded } = await import(
              '../services/migrations/participantUserIdsBackfill'
            );
            await runParticipantUserIdsBackfillIfNeeded(user.uid);
          } catch (error) {
            console.warn('participantUserIds migration skipped:', error);
          }
          // Publish current globalStreak to the user doc so tournament opponents
          // can render it. Sparse historically — many users never had it written
          // because the field is only updated on level completion.
          // Longest streak is computed once here (for the globalStreak write)
          // and reused for the public-stats publish below.
          let longestStreak = 0;
          try {
            const [{ loadDailyActivities }, { computeStreakWithFreezes }, { loadStreakConfig }, { userDataService }, { makeFreezeToastHandler }, { toast }] = await Promise.all([
              import('../utils/globalStreakUtils'),
              import('../utils/streakWithFreezes'),
              import('../utils/streakUtils'),
              import('../services/UserDataService'),
              import('../utils/freezeEventToast'),
              import('../components/notifications/ToastContainer'),
            ]);
            const [dailyActivities, streakConfig] = await Promise.all([
              loadDailyActivities(user.uid),
              loadStreakConfig(),
            ]);
            // Reconcile on login so freezes apply when the user simply
            // reopens the app after a missed day — without this, freezes
            // only fired on the next level completion, leaving the streak
            // displayed as "lost" until then.
            const streak = await computeStreakWithFreezes(
              user.uid,
              dailyActivities,
              streakConfig.levelsRequiredPerDay,
              { reconcile: true, onNewFreezeEvent: makeFreezeToastHandler(toast) },
            );
            longestStreak = streak.longestStreak ?? 0;
            await userDataService.updateUserProfile(user.uid, {
              globalStreak: streak.consecutiveDays,
            });
          } catch (error) {
            console.warn('globalStreak backfill skipped:', error);
          }
          // Publish aggregated public stats (points, levels, time played,
          // longest streak) to the user doc so friends can render them.
          // Separate try so a stats failure never blocks the streak write.
          const { publishPublicStats } = await import('../utils/publicStatsUtils');
          await publishPublicStats(user.uid, longestStreak);
        })();
      } else {
        // Stop sync when user logs out
        stopDataSync();

        setAuthState({
          user: null,
          loading: false,
          syncing: false,
          error: null,
          accessStatus: 'unknown',
        });
      }
    });

    return () => unsubscribe();
  }, [authService]);

  // Reconcile freezes when the app regains focus. Covers the case where the
  // app was left open across midnight: the missed day only becomes an
  // evaluatable past day after the rollover, and without this the freeze
  // wouldn't fire (and the streak would show as "lost") until the next level
  // completion. computeStreakWithFreezes is internally gated to reconcile at
  // most once per calendar day, so re-fired focus events are cheap no-ops.
  useEffect(() => {
    const uid = authState.user?.uid;
    if (!uid) return;

    const reconcileOnFocus = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const [{ loadDailyActivities }, { computeStreakWithFreezes }, { loadStreakConfig }, { makeFreezeToastHandler }, { toast }] =
          await Promise.all([
            import('../utils/globalStreakUtils'),
            import('../utils/streakWithFreezes'),
            import('../utils/streakUtils'),
            import('../utils/freezeEventToast'),
            import('../components/notifications/ToastContainer'),
          ]);
        const [dailyActivities, streakConfig] = await Promise.all([
          loadDailyActivities(uid),
          loadStreakConfig(),
        ]);
        await computeStreakWithFreezes(
          uid,
          dailyActivities,
          streakConfig.levelsRequiredPerDay,
          { reconcile: true, onNewFreezeEvent: makeFreezeToastHandler(toast) },
        );
      } catch (error) {
        console.warn('Focus freeze reconcile skipped:', error);
      }
    };

    document.addEventListener('visibilitychange', reconcileOnFocus);
    window.addEventListener('focus', reconcileOnFocus);
    return () => {
      document.removeEventListener('visibilitychange', reconcileOnFocus);
      window.removeEventListener('focus', reconcileOnFocus);
    };
  }, [authState.user?.uid]);

  const signInWithGoogle = async (): Promise<User> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      // Just trigger the popup. Don't flip loading:false here — the
      // onAuthStateChanged subscription does the full sync (access gate,
      // profile ensure, Firestore→local pull, backfills) and is the
      // authoritative state-flipper. Returning early was causing the
      // dashboard to mount with empty caches and visibly fill in over the
      // next ~1-2 seconds as the sync caught up.
      const user = await authService.signInWithGoogle();
      return user;
    } catch (error) {
      const authError = error as Error;
      setAuthState(prev => ({ ...prev, loading: false, syncing: false, error: authError }));
      throw error;
    }
  };

  const refreshAccessStatus = async (): Promise<void> => {
    if (!authState.user) return;
    const accessStatus = await resolveAccessStatus(authState.user);
    setAuthState(prev => ({ ...prev, accessStatus }));
  };

  const signOut = async (): Promise<void> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));

      // Skip the pre-logout sync when the user never gained app access — they
      // never created data to sync, and the writes would just fail rules.
      const skipPreLogoutSync = authState.accessStatus !== 'allowed';

      // CRITICAL: Sync all data to Firestore before logout
      if (authState.user && !skipPreLogoutSync) {
        console.log('🔄 Syncing data before logout...');
        try {
          const { syncAllDataBeforeLogout } = await import('../utils/logoutSyncUtils');
          const syncResult = await syncAllDataBeforeLogout(authState.user.uid);
          
          if (syncResult.success) {
            console.log('✅ Data sync complete');
          } else {
            console.warn('⚠️ Data sync completed with errors:', syncResult.errors);
            
            // Show user a warning but allow logout to proceed
            const shouldContinue = await confirmAction({
              title: 'Logout with sync errors?',
              message: `Some data may not have synced to the cloud (${syncResult.errors.length} errors). Logout anyway?\n\nYour data is still saved locally and will sync next time you login.`,
              confirmLabel: 'Logout',
              danger: true,
            });
            
            if (!shouldContinue) {
              setAuthState(prev => ({ ...prev, loading: false }));
              return;
            }
          }
        } catch (error) {
          console.error('⚠️ Error syncing data before logout:', error);
          
          // Show user a warning
          const shouldContinue = await confirmAction({
            title: 'Sync failed',
            message: 'Failed to sync data to the cloud. Logout anyway?\n\nYour data is still saved locally and will sync next time you login.',
            confirmLabel: 'Logout',
            danger: true,
          });
          
          if (!shouldContinue) {
            setAuthState(prev => ({ ...prev, loading: false }));
            return;
          }
        }
      }
      
      // Stop sync when user logs out
      stopDataSync();

      await authService.signOut();
      setAuthState({ user: null, loading: false, syncing: false, error: null, accessStatus: 'unknown' });
    } catch (error) {
      const authError = error as Error;
      setAuthState(prev => ({ ...prev, loading: false, error: authError }));
      throw error;
    }
  };

  const value: AuthContextType = {
    ...authState,
    signInWithGoogle,
    signOut,
    authService,
    refreshAccessStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

